import { defineStore } from 'pinia';
import { api } from '@/services/http.service';
import { LimitExceptionsUploadResponse } from '@/models/limit-exceptions-upload/limit-exceptions-upload.model';
import { limitExceptionsUploadErrorItemApiToDto }
  from '@/mappers/limit-exceptions-upload/limit-exceptions-upload-error-item-api-to-dto.mapper';
import { showErrorNotification, showSuccessNotification } from '@/utils/show-notification.util';

export interface LimitExceptionsUploadStoreState {
  isUploadLoading: boolean;
  uploadErrors: string[];
  isUploadModalOpen: boolean;
  uploadedFile: File | null;
  totalRowsReceived: number;
  totalRowsProcessedSuccessfully: number;
}

export const useLimitExceptionsUploadStore = defineStore('limitExceptionsUploadStore', {
  state: (): LimitExceptionsUploadStoreState => ({
    isUploadLoading: false,
    uploadErrors: [],
    isUploadModalOpen: false,
    uploadedFile: null,
    totalRowsReceived: 0,
    totalRowsProcessedSuccessfully: 0,
  }),
  getters: {
    isUploadErrorsPresent: (state: LimitExceptionsUploadStoreState): boolean => {
      return state.uploadErrors.length > 0;
    },
  },
  actions: {
    setUploadLoading(isLoading: boolean): void {
      this.isUploadLoading = isLoading;
    },
    setUploadModalOpen(isOpen: boolean): void {
      this.isUploadModalOpen = isOpen;
    },
    setUploadedFile(file: File | null): void {
      this.uploadedFile = file;
    },
    clearUploadErrors(): void {
      this.uploadErrors = [];
      this.totalRowsReceived = 0;
      this.totalRowsProcessedSuccessfully = 0;
    },
    resetUploadState(): void {
      this.$reset();
    },
    closeUploadModal(): void {
      this.isUploadModalOpen = false;
      this.resetUploadState();
    },
    uploadLimitExceptionsFile(): Promise<LimitExceptionsUploadResponse | void> {
      if (!this.uploadedFile) {
        return Promise.resolve();
      }

      this.setUploadLoading(true);
      this.uploadErrors = [];
      this.totalRowsReceived = 0;
      this.totalRowsProcessedSuccessfully = 0;

      const formData = new FormData();
      formData.append('file', this.uploadedFile);

      return api.limitExceptionsUpload.uploadLimitExceptionsRequest
        .perform(formData)
        .then((response: LimitExceptionsUploadResponse): LimitExceptionsUploadResponse => {
          const { totalRowsReceived, totalRowsProcessedSuccessfully, errors } = response;

          this.totalRowsReceived = totalRowsReceived;
          this.totalRowsProcessedSuccessfully = totalRowsProcessedSuccessfully;

          if (errors && errors.length > 0) {
            this.uploadErrors = errors.map(limitExceptionsUploadErrorItemApiToDto);
          } else {
            this.closeUploadModal();
            showSuccessNotification('Limit exceptions added');
          }

          return response;
        })
        .catch((error: unknown): void => {
          showErrorNotification(error);
        })
        .finally((): void => {
          this.setUploadLoading(false);
        });
    },
  },
});
